/**
 * @tokenstreaming/shared — Chain registry, contract addresses, ICE config.
 * All chain references use CAIP-2 (eip155:56, eip155:97). No bare integers.
 */

export interface TokenConfig {
  readonly address: string; // raw 0x address (chain context from parent)
  readonly symbol: string;
  readonly decimals: number;
  readonly name: string;
}

export interface ChainConfig {
  readonly chainId: string; // CAIP-2: "eip155:56"
  readonly name: string;
  readonly rpcUrls: readonly string[];
  readonly explorerUrl: string;
  readonly nativeSymbol: string;
  readonly nativeDecimals: number;
  readonly contracts: {
    readonly qr20Channel: string;
  };
  readonly tokens: readonly TokenConfig[];
}

export interface IceServerConfig {
  readonly urls: string | string[];
  readonly username?: string;
  readonly credential?: string;
}

export interface AppConfig {
  readonly chains: Record<string, ChainConfig>;
  readonly defaultChain: string;
  readonly iceServers: readonly IceServerConfig[];
  readonly streaming: {
    readonly tickMs: number;
    readonly settleEveryMs: number;
    readonly defaultDisputeWindowS: number;
  };
  readonly distribution: {
    readonly platformAddress: string;
    readonly platformBps: number;
    readonly affiliateBps: number;
  };
}

export const CHAINS: Record<string, ChainConfig> = {
  'eip155:56': {
    chainId: 'eip155:56',
    name: 'BSC Mainnet',
    rpcUrls: [
      'https://bsc-dataseed1.binance.org',
      'https://bsc-dataseed2.binance.org',
      'https://bsc-dataseed3.binance.org',
      'https://bsc-dataseed4.binance.org',
    ],
    explorerUrl: 'https://bscscan.com',
    nativeSymbol: 'BNB',
    nativeDecimals: 18,
    contracts: {
      qr20Channel: '0xb58d6b898AE8f45321ed7Fe84d7788c078be5022',
    },
    tokens: [
      {
        address: '0x55d398326f99059fF775485246999027B3197955',
        symbol: 'USDT',
        decimals: 18,
        name: 'Tether USD',
      },
      {
        address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
        symbol: 'USDC',
        decimals: 18,
        name: 'USD Coin',
      },
      {
        address: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56',
        symbol: 'BUSD',
        decimals: 18,
        name: 'Binance USD',
      },
    ],
  },
  'eip155:97': {
    chainId: 'eip155:97',
    name: 'BSC Testnet',
    rpcUrls: [
      'https://data-seed-prebsc-1-s1.binance.org:8545',
      'https://data-seed-prebsc-2-s1.binance.org:8545',
    ],
    explorerUrl: 'https://testnet.bscscan.com',
    nativeSymbol: 'tBNB',
    nativeDecimals: 18,
    contracts: {
      qr20Channel: '0x0000000000000000000000000000000000000000', // not deployed on testnet
    },
    tokens: [
      {
        address: '0x337610d27c682E347C9cD60BD4b3b107C9d34dDd',
        symbol: 'TST',
        decimals: 18,
        name: 'Test Token',
      },
    ],
  },
};

export const DEFAULT_ICE_SERVERS: readonly IceServerConfig[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

export const DEFAULT_STREAMING_PARAMS = {
  tickMs: 1000,
  settleEveryMs: 30000,
  defaultDisputeWindowS: 3600,
} as const;

export function getChainConfig(chainId: string): ChainConfig {
  const config = CHAINS[chainId];
  if (!config) {
    throw new Error(`Unknown chain: ${chainId}. Supported: ${Object.keys(CHAINS).join(', ')}`);
  }
  return config;
}

export function getAssetRef(chainId: string, tokenAddress: string): string {
  return `${chainId}/erc20:${tokenAddress}`;
}

export function getContractRef(chainId: string, contractAddress: string): string {
  return `${chainId}/${contractAddress}`;
}

export function getAccountRef(chainId: string, address: string): string {
  return `${chainId}/${address}`;
}
